import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/ui/AuthenticationGuard';
import Icon from '../../components/AppIcon';

import AuthMethodSelector from './components/AuthMethodSelector';
import LoginForm from './components/LoginForm';
import LanguageToggle from './components/LanguageToggle';
import SecurityBadges from './components/SecurityBadges';

const Login = () => {
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [authMethod, setAuthMethod] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    localStorage.setItem('preferredLanguage', language);
  };

  const handleLogin = async (formData) => {
    setIsLoading(true);

    try {
      // Simulate authentication
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const userData = {
        id: 'user_123',
        name: 'Jean Dupont',
        email: formData?.email || `+237${formData?.phone}`,
        phone: formData?.phone || '677123456',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a204867b-1763291659906.png",
        avatarAlt: 'Professional headshot of African man with short hair in business attire',
        role: 'user',
        verified: true,
        joinDate: '2024-01-15',
        location: 'Douala, Cameroun',
        authMethod: authMethod,
        language: currentLanguage
      };

      const token = 'mock_jwt_token_' + Date.now();

      login(userData, token);

      // Navigate to intended destination or dashboard
      const intendedPath = localStorage.getItem('intendedPath');
      if (intendedPath) {
        localStorage.removeItem('intendedPath');
        navigate(intendedPath);
      } else {
        navigate('/homepage-dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert(currentLanguage === 'fr' ? 'Fonctionnalité de récupération de mot de passe à venir' : 'Password recovery feature coming soon'
    );
  };

  const handleSwitchToRegister = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="w-full bg-card border-b border-border shadow-soft">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/homepage-dashboard')}>

              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mr-2">
                <Icon name="Car" size={20} color="white" />
              </div>
              <span className="text-xl font-bold text-foreground">AutoCasse</span>
            </div>
            
            {/* Back Button */}
            <button
              onClick={() => navigate('/homepage-dashboard')}
              className="p-2 text-muted-foreground hover:text-foreground transition-smooth">

              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Welcome Section */}
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Icon name="LogIn" size={32} className="text-primary" />
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {currentLanguage === 'fr' ? 'Connexion' : 'Sign In'}
              </h1>
              <p className="text-muted-foreground">
                {currentLanguage === 'fr' ? 'Accédez à votre compte AutoCasse' : 'Access your AutoCasse account'
                }
              </p>
            </div>
          </div>

          {/* Language Toggle */}
          <LanguageToggle
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange} />


          {/* Authentication Method Selector */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-soft">
            <AuthMethodSelector
              selectedMethod={authMethod}
              onMethodChange={setAuthMethod}
              currentLanguage={currentLanguage} />

          </div>

          {/* Login Form */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-soft">
            <LoginForm
              authMethod={authMethod}
              currentLanguage={currentLanguage}
              onSubmit={handleLogin}
              isLoading={isLoading}
              onForgotPassword={handleForgotPassword}
              onSwitchToRegister={handleSwitchToRegister} />

          </div>

          {/* Security Badges */}
          <SecurityBadges currentLanguage={currentLanguage} />

          {/* Footer */}
          <div className="text-center space-y-2 pt-4">
            <p className="text-xs text-muted-foreground">
              {currentLanguage === 'fr' ? 'En vous connectant, vous acceptez nos' : 'By signing in, you agree to our'
              }
            </p>
            <div className="flex items-center justify-center space-x-4 text-xs">
              <button className="text-primary hover:text-primary/80 transition-smooth">
                {currentLanguage === 'fr' ? 'Conditions' : 'Terms'}
              </button>
              <span className="text-muted-foreground">•</span>
              <button className="text-primary hover:text-primary/80 transition-smooth">
                {currentLanguage === 'fr' ? 'Confidentialité' : 'Privacy'}
              </button>
            </div>
            
            <div className="flex items-center justify-center space-x-2 pt-2">
              <Icon name="Shield" size={12} className="text-success" />
              <span className="text-xs text-muted-foreground">
                © {new Date()?.getFullYear()} AutoCasse - {currentLanguage === 'fr' ? 'Plateforme sécurisée' : 'Secure platform'}
              </span>
            </div>
          </div>
        </div>
      </main>
      {/* Mobile Bottom Padding */}
      <div className="h-16 md:hidden"></div>
    </div>);

};

export default Login;